import * as React from 'react';
import { runInAction } from 'mobx';
import isString from 'lodash/isString';
import isNumber from 'lodash/isNumber';
import classNames from 'classnames';
import { LAYER_WIDTH } from './constants';
import { defaultClassPrefix, getUnhandledProps, isNullOrUndefined, prefix } from './utils';
import TableContext from './TableContext';
import { ColumnPropTypeKeys } from './Column';
import isEmpty from '../_util/isEmpty';
export const propTypeKeys = [
    'align',
    'verticalAlign',
    'className',
    'classPrefix',
    'dataKey',
    'isHeaderCell',
    'width',
    'height',
    'left',
    'headerHeight',
    'style',
    'firstColumn',
    'lastColumn',
    'hasChildren',
    'children',
    'rowKey',
    'rowIndex',
    'rowData',
    'depth',
    'onTreeToggle',
    'renderTreeToggle',
    'renderCell',
    'wordWrap',
    'hidden',
    'treeCol',
    'expanded',
    'groupHeader',
    'groupCount',
    'isDragging',
];
class Cell extends React.PureComponent {
    constructor() {
        super(...arguments);
        // @ts-ignore
        this.addPrefix = (name) => prefix(this.props.classPrefix)(name);
        this.handleExpandClick = (event) => {
            const { rowKey, rowIndex, rowData, onTreeToggle } = this.props;
            if (onTreeToggle) {
                onTreeToggle(rowKey, rowIndex, rowData, event);
            }
        };
    }
    static get contextType() {
        return TableContext;
    }
    isTreeCol() {
        const { treeCol, firstColumn } = this.props;
        const { hasCustomTreeCol, isTree } = this.context;
        if (treeCol) {
            return true;
        }
        if (!hasCustomTreeCol && firstColumn && isTree) {
            return true;
        }
        return false;
    }
    getHeight() {
        const { height, rowData } = this.props;
        // @ts-ignore
        return typeof height === 'function' ? height(rowData) : height;
    }
    renderTreeNodeExpandIcon() {
        const { rowData, renderTreeToggle, hasChildren, expanded } = this.props;
        const expandButton = React.createElement("i", { className: this.addPrefix('expand-icon icon') });
        if (this.isTreeCol() && hasChildren) {
            return (React.createElement("span", { role: "button", tabIndex: -1, className: this.addPrefix('expand-wrapper'), onClick: this.handleExpandClick }, renderTreeToggle ? renderTreeToggle(expandButton, rowData, expanded) : expandButton));
        }
        return null;
    }
    render() {
        const { width, left, style, className, firstColumn, lastColumn, isHeaderCell, headerHeight, align, children, rowData, dataKey, rowIndex, renderCell, hidden, wordWrap, classPrefix, depth, verticalAlign, expanded, onClick, // Fix sortColumn not getting fired in Gatsby production build
        onCell, rowSpan, fixed, hasChildren, ...rest } = this.props;
        const { tableStore } = this.context;
        const cellExternalProps = (typeof onCell === 'function'
            ? onCell({
                rowData,
                dataKey,
                rowIndex,
            })
            : {}) || {};
        const cellHidden = hidden || cellExternalProps.hidden;
        const cellRowSpan = rowSpan || cellExternalProps.rowSpan;
        if (cellHidden || cellRowSpan === 0) {
            if (!isHeaderCell && rowData && cellRowSpan === 0 && fixed) {
                runInAction(() => {
                    tableStore.rowZIndex.push(rowIndex);
                });
            }
            return null;
        }
        const classes = classNames(classPrefix, className, cellExternalProps.className, {
            [this.addPrefix('expanded')]: expanded && this.isTreeCol(),
            [this.addPrefix('first')]: firstColumn,
            [this.addPrefix('last')]: lastColumn,
        });
        const { rtl } = this.context;
        let nextHeight = isHeaderCell ? headerHeight : this.getHeight();
        if (cellExternalProps.rowSpan) {
            nextHeight = cellExternalProps.rowSpan * (nextHeight || 1);
        }
        const styles = {
            ...style,
            ...cellExternalProps.style,
            width,
            height: nextHeight,
            zIndex: depth,
            [rtl ? 'right' : 'left']: left,
        };
        //By moving style from cell content to Cell, it breaks UI behaviours.
        // for example, check box are not showing in the middle of the cell anymore.
        const contentStyles = {
            width,
            height: nextHeight,
            textAlign: align,
            [rtl ? 'paddingRight' : 'paddingLeft']: this.isTreeCol() ? depth * LAYER_WIDTH + (hasChildren ? 8 : 36) : null,
        };
        if (verticalAlign) {
            contentStyles.display = 'table-cell';
            contentStyles.verticalAlign = verticalAlign;
        }
        // @ts-ignore
        let cellContent = isNullOrUndefined(children) && rowData ? rowData[dataKey] : children;
        if (typeof children === 'function') {
            const getChildren = children;
            cellContent = getChildren(rowData, rowIndex);
        }
        // fix unable to get propTypes after gatsby is compiled
        const unhandledProps = getUnhandledProps(propTypeKeys, getUnhandledProps(ColumnPropTypeKeys, rest));
        let cell = renderCell ? renderCell(cellContent) : cellContent;
        const { searchText, highlightRowIndexs } = tableStore;
        if (isEmpty(cell)) {
            cell = tableStore.getConfig('renderEmpty')('Output');
        }
        if (isNumber(cell))
            cell = String(cell);
        if (isString(cell) && searchText) {
            const index = cell.indexOf(searchText);
            const beforeStr = cell.substr(0, index);
            const afterStr = cell.substr(index + searchText.length);
            if (index > -1) {
                highlightRowIndexs.push(rowIndex);
            }
            cell = index > -1 ? (React.createElement("span", null,
                beforeStr,
                React.createElement("span", { className: this.addPrefix('letter-highlight') }, searchText),
                afterStr)) : React.createElement("span", null, cell);
        }
        const content = wordWrap ? (React.createElement("div", { className: this.addPrefix('wrap') },
            this.renderTreeNodeExpandIcon(),
            cell)) : (React.createElement(React.Fragment, null,
            this.renderTreeNodeExpandIcon(),
            cell));
        return (React.createElement("div", Object.assign({ role: isHeaderCell ? 'columnheader' : 'gridcell' }, unhandledProps, { onClick: onClick, className: classes, style: styles }),
            React.createElement("div", { className: this.addPrefix('content'), style: contentStyles }, content)));
    }
}
Cell.defaultProps = {
    classPrefix: defaultClassPrefix('performance-table-cell'),
    headerHeight: 36,
    depth: 0,
    height: 36,
    width: 0,
    left: 0,
};
export default Cell;
